import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { AccessList } from "../../../api/access/accessModel";
import PersonAvatar from "../../../ui/avatars/PersonAvatar";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import Field from "../../../ui/forms/Field";
import ListCard from "../../../ui/layout/ListCard";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { GroupNamer } from "../model/groupNames";
import { viaGroups } from "../model/groupOrder";

interface PeopleListProps {
  list: AccessList;
  namer: GroupNamer;
  /** Opens one person's access panel. */
  onOpen: (userId: string) => void;
  /** "Add people", for those who manage the list. */
  action?: ReactNode;
  /** Shown above the list, such as the form that adds people. */
  children?: ReactNode;
}

/** Everyone the project's groups reach, with their groups; one click opens a person's access. */
export default function PeopleList({ list, namer, onOpen, action, children }: PeopleListProps) {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState("");
  const wanted = filter.trim().toLocaleLowerCase();
  const name = (userId: string) => list.namedPeople.get(userId)?.displayName ?? "";
  const matches = (userId: string) => {
    const known = list.namedPeople.get(userId);
    return known !== undefined && (known.displayName.toLocaleLowerCase().includes(wanted) || known.email.toLocaleLowerCase().includes(wanted));
  };
  const shown = list.people
    .filter((person) => wanted === "" || matches(person.userId))
    .sort((left, right) => name(left.userId).localeCompare(name(right.userId), i18n.language));

  return (
    <ListCard label={t("access.people")} actions={action} lead={(
      <Field label={t("access.findPerson")} labelHidden={true}>
        {(control) => (
          <input {...control} type="search" className={`${FORM_CONTROL_CLASS} max-w-60`} placeholder={t("access.findPerson")} value={filter}
            onChange={(event) => { setFilter(event.target.value); }} />
        )}
      </Field>
    )}>
      {children}
      {shown.length === 0 ? <EmptyState size="small" className="px-4 pb-3">{t("access.nobody")}</EmptyState> : (
        <ul className="app-divide-y">
          {shown.map((person) => {
            const known = list.namedPeople.get(person.userId);
            return (
              <li key={person.userId}>
                <button type="button" className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-[var(--app-surface-muted)]"
                  onClick={() => { onOpen(person.userId); }}>
                  <PersonAvatar userId={person.userId} name={known?.displayName ?? ""} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{known?.displayName}</span>
                    <span className="app-text-muted block truncate text-xs">
                      {known !== undefined && !known.active ? `${known.email} · ${t("access.panel.inactive")}` : known?.email}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center justify-end gap-1">
                    {person.groupIds.map((groupId) => {
                      // A group that reaches them through a group inside it says which, as its members' list does (Sol critique C11).
                      const inner = viaGroups(list.people, person.userId, groupId);
                      return (
                        <span key={groupId} className="inline-flex items-center gap-1">
                          <Chip>{namer.name(groupId)}</Chip>
                          {inner === null ? null : <span className="app-text-muted text-xs">{t("access.via", { group: namer.path(inner) })}</span>}
                        </span>
                      );
                    })}
                    {person.itemCurations.length > 0 ? <Chip>{t("access.itemCurator", { count: person.itemCurations.length })}</Chip> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </ListCard>
  );
}
